import { DISC_BADGE, DISC_LETTER } from './constants.js';
import { savePref } from './preferences.js';

const VIEW_MODES = ['map', 'split', 'text'];
const TEXT_MODES = ['incidents', 'messages'];

/** Hoeveel losse P2000-berichten we maximaal in de berichtenmodus bewaren. */
const MAX_RAW_MESSAGES = 400;

/** Discipline → CSS-suffix, hergebruikt voor de randkleur en de capcode-tags. */
const DISC_SUFFIX = {
    Brandweer: 'bw',
    Ambulance: 'amb',
    Politie: 'pol',
    Lifeliner: 'life',
    KNRM: 'knrm',
    Reddingsbrigade: 'knrm',
    Veiligheidsregio: 'vr',
    Meldkamer: 'vr',
};

/**
 * Tekstweergave van /app — dezelfde meldingen zonder kaart.
 *
 * Twee submodi:
 * - incidents: onze gegroepeerde incidenten, met dezelfde filters als de sidebar.
 * - messages:  de ruwe P2000-berichten zoals ze binnenkomen, met capcodes.
 *
 * Wordt met Object.assign in App gemengd; gebruikt App.incidents, passesFilter(),
 * selectIncident(), esc() en formatTime() zodat er geen tweede filter- of
 * formatteerlaag ontstaat.
 */
export const TextView = {
    viewMode: 'split',
    textMode: 'incidents',
    rawMessages: [],
    _rawLoaded: false,
    _rawLoading: false,
    showCapcodes: true,

    /** Hoe lang een melding als "nieuw" gemarkeerd blijft (seconden, 0 = uit). */
    freshWindowSec: 60,

    initTextView() {
        const savedView = localStorage.getItem('viewMode');
        this.viewMode = VIEW_MODES.includes(savedView) ? savedView : 'split';
        const savedText = localStorage.getItem('textMode');
        this.textMode = TEXT_MODES.includes(savedText) ? savedText : 'incidents';
        this.showCapcodes = localStorage.getItem('txCapcodes') !== '0';
        const savedFresh = parseInt(localStorage.getItem('freshWindow') ?? '', 10);
        if (!isNaN(savedFresh) && savedFresh >= 0) this.freshWindowSec = savedFresh;

        document.querySelectorAll('.vsw-btn').forEach(btn => {
            btn.addEventListener('click', () => this.setViewMode(btn.dataset.view));
        });
        document.querySelectorAll('.tx-mode').forEach(btn => {
            btn.addEventListener('click', () => this.setTextMode(btn.dataset.tmode));
        });

        const capBtn = document.getElementById('txCapcodes');
        if (capBtn) {
            capBtn.addEventListener('click', () => {
                this.showCapcodes = !this.showCapcodes;
                localStorage.setItem('txCapcodes', this.showCapcodes ? '1' : '0');
                this._syncTextChrome();
            });
        }

        // Klikdelegatie: rijen worden bij elke render opnieuw gebouwd
        const body = document.getElementById('txBody');
        if (body) {
            body.addEventListener('click', (e) => {
                const row = e.target.closest('.tr');
                if (!row || row.dataset.noclick || !row.dataset.id) return;
                this.onCardClick(Number(row.dataset.id));
            });
        }

        const freshSel = document.getElementById('freshWindowSelect');
        if (freshSel) {
            freshSel.value = String(this.freshWindowSec);
            freshSel.addEventListener('change', () => {
                this.freshWindowSec = parseInt(freshSel.value, 10) || 0;
                savePref('freshWindow', String(this.freshWindowSec));
                this.sweepFresh();
            });
        }

        // De markering loopt op de klok, niet op het moment van binnenkomst: zo
        // klopt hij ook na een herlaad en verdwijnt hij zonder de lijst te
        // hertekenen (dat zou de scrollpositie en de selectie verstoren).
        setInterval(() => this.sweepFresh(), 2000);

        this.applyViewMode(false);
    },

    /** Epoch-ms uit een DB-timestamp ('2026-08-25 10:26:20.227', lokale tijd). */
    _timestampMs(value) {
        if (!value) return null;
        const t = new Date(String(value).replace(' ', 'T')).getTime();
        return isNaN(t) ? null : t;
    },

    /**
     * Zet of verwijder de nieuw-markering op basis van de klok.
     * Werkt direct op de DOM zodat een lopende lijst niet opnieuw wordt gebouwd.
     */
    sweepFresh() {
        if (this.viewMode !== 'text') return;
        const body = document.getElementById('txBody');
        if (!body) return;

        if (!this.freshWindowSec) {
            body.querySelectorAll('.tr.tr-fresh').forEach(row => row.classList.remove('tr-fresh'));
            return;
        }

        const cutoff = Date.now() - this.freshWindowSec * 1000;
        body.querySelectorAll('.tr[data-ts]').forEach(row => {
            row.classList.toggle('tr-fresh', Number(row.dataset.ts) >= cutoff);
        });
    },

    _freshClass(value) {
        if (!this.freshWindowSec) return '';
        const ts = this._timestampMs(value);
        return ts !== null && Date.now() - ts <= this.freshWindowSec * 1000 ? ' tr-fresh' : '';
    },

    setViewMode(mode) {
        if (!VIEW_MODES.includes(mode) || mode === this.viewMode) return;
        this.viewMode = mode;
        savePref('viewMode', mode);
        this.applyViewMode(true);
    },

    setTextMode(mode) {
        if (!TEXT_MODES.includes(mode) || mode === this.textMode) return;
        this.textMode = mode;
        savePref('textMode', mode);
        this._syncRawSubscription();
        if (mode === 'messages') this.loadRawMessages();
        this._syncTextChrome();
        this.renderTextView();
    },

    applyViewMode(animate) {
        const dash = document.querySelector('.dash');
        if (dash) {
            VIEW_MODES.forEach(m => dash.classList.toggle('view-' + m, m === this.viewMode));
        }
        document.querySelectorAll('.vsw-btn').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.view === this.viewMode);
        });

        this._syncRawSubscription();
        this._syncTextChrome();

        if (this.viewMode === 'text') {
            if (this.textMode === 'messages') this.loadRawMessages();
            this.renderTextView();
        } else if (this.map) {
            // De kaart stond verborgen en kent zijn nieuwe formaat nog niet
            setTimeout(() => this.map.invalidateSize(), animate ? 60 : 0);
        }
    },

    _syncTextChrome() {
        document.querySelectorAll('.tx-mode').forEach(btn => {
            btn.classList.toggle('active', btn.dataset.tmode === this.textMode);
        });
        const hint = document.getElementById('txHint');
        if (hint) {
            hint.textContent = this.textMode === 'incidents'
                ? 'Gegroepeerd per incident'
                : 'Losse P2000-berichten, ongegroepeerd';
        }
        const capBtn = document.getElementById('txCapcodes');
        if (capBtn) capBtn.classList.toggle('active', this.showCapcodes);
        const body = document.getElementById('txBody');
        if (body) body.classList.toggle('hide-caps', !this.showCapcodes);
    },

    /** Ruwe berichten alleen over de WebSocket vragen wanneer we ze tonen. */
    _syncRawSubscription() {
        const want = this.viewMode === 'text' && this.textMode === 'messages';
        if (want === this._rawSubscribed) return;
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            this._rawSubscribed = undefined;
            return;
        }
        this.ws.send(JSON.stringify({ type: 'raw_messages', enabled: want }));
        this._rawSubscribed = want;
    },

    /** Na een herverbinding kunnen berichten gemist zijn — opnieuw ophalen. */
    resetRawMessages() {
        this._rawLoaded = false;
        if (this.viewMode === 'text' && this.textMode === 'messages') this.loadRawMessages();
    },

    async loadRawMessages() {
        if (this._rawLoaded || this._rawLoading) return;
        this._rawLoading = true;
        try {
            const res = await fetch('/api/v1/messages?per_page=150');
            const json = await res.json();
            if (Array.isArray(json.data)) {
                // API levert nieuwste eerst; dat is ook onze weergavevolgorde
                this.rawMessages = json.data;
                this._rawLoaded = true;
            }
        } catch (e) {
            console.error('Kan berichten niet laden:', e);
        }
        this._rawLoading = false;
        if (this.viewMode === 'text' && this.textMode === 'messages') this.renderTextView();
    },

    /** WebSocket message.new — alleen ontvangen na opt-in. */
    handleMessageEvent(data) {
        if (!data || !data.id) return;
        if (this.rawMessages.some(m => String(m.id) === String(data.id))) return;
        this.rawMessages.unshift(data);
        if (this.rawMessages.length > MAX_RAW_MESSAGES) {
            this.rawMessages.length = MAX_RAW_MESSAGES;
        }
        if (this.viewMode === 'text' && this.textMode === 'messages') this.renderTextView();
    },

    renderTextView() {
        if (this.viewMode !== 'text') return;
        const body = document.getElementById('txBody');
        const empty = document.getElementById('txEmpty');
        const count = document.getElementById('txCount');
        if (!body) return;

        const rows = this.textMode === 'incidents'
            ? this._textIncidentRows()
            : this._textMessageRows();

        if (count) count.textContent = String(rows.count);

        if (rows.html === '') {
            body.innerHTML = '<div class="tx-empty">' + rows.emptyLabel + '</div>';
            return;
        }
        if (empty) empty.style.display = 'none';

        // Nieuwe regels komen bovenaan binnen. Staat de gebruiker verder naar
        // beneden, dan schuiven we mee met de toegevoegde hoogte zodat de regel
        // die hij las op zijn plek blijft.
        const prevScroll = body.scrollTop;
        const prevHeight = body.scrollHeight;
        body.innerHTML = rows.html;
        if (prevScroll > 8) {
            body.scrollTop = prevScroll + (body.scrollHeight - prevHeight);
        }
    },

    _textIncidentRows() {
        const visible = [];
        this.incidents.forEach(inc => {
            if (this.passesFilter(inc)) visible.push(inc);
        });
        visible.sort((a, b) => {
            const tA = a.last_message_at || a.first_message_at || '';
            const tB = b.last_message_at || b.first_message_at || '';
            return tB.localeCompare(tA);
        });

        const html = visible.map(inc => this._incidentRowHtml(inc)).join('');
        return {
            html,
            count: visible.length,
            emptyLabel: this.filtersApplied ? 'Geen incidenten voor deze filters' : 'Wachten op meldingen...',
        };
    },

    _incidentRowHtml(inc) {
        const discs = inc.disciplines || [];
        const primary = discs[0] || '';
        const suffix = DISC_SUFFIX[primary] || '';

        let badges = '';
        discs.forEach(d => {
            badges += `<span class="disc-letter ${DISC_BADGE[d] || 'b-over'}">${DISC_LETTER[d] || d.substring(0, 1)}</span>`;
        });
        if (inc.grip) {
            badges += `<span class="prio-badge b-grip2">GRIP${inc.grip}</span>`;
        } else if (inc.priority_code) {
            badges += `<span class="prio-badge ${this.prioBadgeClass(inc.priority_code)}">${this.esc(inc.priority_code)}</span>`;
        }

        const sub = [];
        const type = inc.incident_type || '';
        if (type) sub.push(`<span class="tr-type">${this.esc(type)}</span>`);
        const place = inc.city || inc.region_name || '';
        if (place) sub.push(`<span>${this.esc(place)}</span>`);
        if (inc.unit_count > 0) sub.push(`<span>${inc.unit_count} eenhe${inc.unit_count === 1 ? 'id' : 'den'}</span>`);
        if (inc.message_count > 1) sub.push(`<span>${inc.message_count} berichten</span>`);

        const stamp = inc.last_message_at || inc.first_message_at;
        let cls = 'tr' + (suffix ? ' tr-' + suffix : '');
        if (this.selected === inc.id) cls += ' sel';
        cls += this._freshClass(stamp);
        if (inc.is_test) cls += ' test';
        if (inc.status === 'closed') cls += ' closed';

        return `<div class="${cls}" data-id="${inc.id}" data-ts="${this._timestampMs(stamp) ?? ''}">
            <span class="tr-time"><i class="tr-new"></i>${this.formatTime(stamp)}</span>
            <span class="badges">${badges}</span>
            <div class="tr-main">
                <div class="tr-body">${this.esc(inc.body || type || '–')}</div>
                ${sub.length ? `<div class="tr-sub">${sub.join('<span class="tr-dot">&middot;</span>')}</div>` : ''}
            </div>
        </div>`;
    },

    /**
     * Filters toepassen op een los P2000-bericht.
     *
     * Discipline, prioriteit, regio en test staan in het bericht zelf — daar wint
     * het bericht, want in een berichtenfeed verwacht je bij filter "B" alleen
     * brandweerregels, ook als het incident meerdere disciplines heeft.
     * Escalatie, locatie en kaartbeeld hangen aan het incident: die halen we bij
     * de bovenliggende melding op en laten we door dezelfde escLevel()/haversine
     * lopen als de kaart, zodat er geen tweede filterimplementatie ontstaat.
     */
    passesMessageFilter(msg) {
        const incidentId = msg.incident_id ? Number(msg.incident_id) : null;
        const inc = incidentId ? this.incidents.get(incidentId) : null;

        if (this.activeFeedId && this.feedIncidents.length > 0) {
            if (incidentId === null || !this.feedIncidents.includes(incidentId)) return false;
        }
        if (this.fHideTest && msg.is_test) return false;
        if (this.fDisc.size > 0 && !this.fDisc.has(msg.discipline || '')) return false;
        if (this.fPrio.size > 0 && !this.fPrio.has((msg.priority_code || '').toUpperCase())) return false;
        if (this.fRegion && String(msg.region_id || '') !== String(this.fRegion)) return false;

        // Escalatieniveau: het incident kent ook het GRIP-niveau, een los bericht
        // alleen zijn eigen opschaling. Incident wint, bericht is de terugval.
        if (this.fEsc.size > 0) {
            const level = this.escLevel(inc ?? { grip: null, escalation_level: msg.escalation_level });
            if (!this.fEsc.has(level)) return false;
        }

        // Locatie en kaartbeeld: coördinaten kennen we alleen op incidentniveau
        const needsCoords = this.fLocLat !== null || (this.mapBoundsFilter && this.map);
        if (needsCoords) {
            if (!inc || !inc.lat || !inc.lng) return false;
            if (this.fLocLat !== null
                && this.haversineKm(this.fLocLat, this.fLocLng, inc.lat, inc.lng) > this.fRadiusKm) {
                return false;
            }
            if (this.mapBoundsFilter && this.map && !this.map.getBounds().contains([inc.lat, inc.lng])) {
                return false;
            }
        }

        // Tijdlijn: een bericht heeft zijn eigen moment
        const cutoff = this.getTimelineCutoff();
        if (cutoff && msg.transmitted_at) {
            const sent = new Date(msg.transmitted_at.replace(' ', 'T'));
            if (!isNaN(sent.getTime()) && sent > cutoff) return false;
        }

        return true;
    },

    _textMessageRows() {
        const list = this.rawMessages.filter(m => this.passesMessageFilter(m));
        const html = list.map(msg => this._messageRowHtml(msg)).join('');
        return {
            html,
            count: list.length,
            emptyLabel: this._rawLoading
                ? 'Berichten laden...'
                : (this.filtersApplied ? 'Geen berichten voor deze filters' : 'Wachten op berichten...'),
        };
    },

    _messageRowHtml(msg) {
        const disc = msg.discipline || '';
        const suffix = DISC_SUFFIX[disc] || '';

        let badges = '';
        if (disc) {
            badges += `<span class="disc-letter ${DISC_BADGE[disc] || 'b-over'}">${DISC_LETTER[disc] || disc.substring(0, 1)}</span>`;
        }
        if (msg.priority_code) {
            badges += `<span class="prio-badge ${this.prioBadgeClass(msg.priority_code)}">${this.esc(msg.priority_code)}</span>`;
        }

        const units = (msg.units || []).map(u => {
            const parsed = this._splitUnitDescription(u.description);
            const tag = parsed.tag
                ? `<span class="tr-tag t-${suffix || 'vr'}">${this.esc(parsed.tag)}</span>`
                : '';
            return `<div class="tr-unit">
                <span class="tr-cap">${this.esc(String(u.capcode))}</span>
                ${tag}
                <span class="tr-unit-desc">${this.esc(parsed.rest)}</span>
            </div>`;
        }).join('');

        const sub = [];
        if (msg.station_label) sub.push(`<span class="tr-type">${this.esc(msg.station_label)}</span>`);
        if (msg.is_cancel) sub.push('<span>Intrekking</span>');

        let cls = 'tr tr-msg' + (suffix ? ' tr-' + suffix : '');
        cls += this._freshClass(msg.transmitted_at);
        if (msg.is_test) cls += ' test';

        // Berichten zonder incident (administratief) zijn niet aanklikbaar
        const attr = msg.incident_id ? ` data-id="${msg.incident_id}"` : ' data-noclick="1"';

        return `<div class="${cls}"${attr} data-ts="${this._timestampMs(msg.transmitted_at) ?? ''}">
            <span class="tr-time"><i class="tr-new"></i>${this.formatTime(msg.transmitted_at)}</span>
            <span class="badges">${badges}</span>
            <div class="tr-main">
                <div class="tr-body">${this.esc(msg.body || '')}</div>
                ${sub.length ? `<div class="tr-sub">${sub.join('<span class="tr-dot">&middot;</span>')}</div>` : ''}
                ${units ? `<div class="tr-units">${units}</div>` : ''}
            </div>
        </div>`;
    },

    /**
     * Selectie in de tekstweergave meebewegen zonder de hele lijst te hertekenen.
     * Meerdere regels kunnen bij hetzelfde incident horen (berichtenmodus).
     */
    _markTextRowSelected(id) {
        const body = document.getElementById('txBody');
        if (!body) return;
        body.querySelectorAll('.tr').forEach(row => {
            row.classList.toggle('sel', id !== null && Number(row.dataset.id) === id);
        });
    },

    /**
     * "RAV Gooi en Vechtstreek (...)" → tag "RAV" + de rest.
     * Alleen een korte hoofdletterafkorting telt als tag.
     */
    _splitUnitDescription(description) {
        const desc = description || '';
        const m = desc.match(/^([A-Z]{2,5})\s+(.+)$/);
        if (m) return { tag: m[1], rest: m[2] };
        return { tag: '', rest: desc };
    },
};
